import {
  BadRequestException,
  ForbiddenException,
  Inject,
  Injectable,
  Logger,
  NotFoundException,
  Optional,
  forwardRef,
} from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService, StorageAdapter, isTransient } from '@cart/api-data-access';
import { MediaOwnerType, assertKeyBelongsTo, buildMediaKey, isPrivateOwnerType, sanitizeFileName } from './media-key.util';
import { countsAgainstQuota } from './platform-storage.constants';
import { ResolvedConfig, StorageAdapterResolver } from './storage-adapter-resolver.service';
import { StorageConfigService } from './storage-config.service';

export interface WriteAssetInput {
  organizationId: string;
  storeId?: string | null;
  ownerType: MediaOwnerType;
  ownerId?: string | null;
  /**
   * Set when the image belongs to one variant rather than the whole product.
   * Becomes a level in the object key; see media-key.util.ts.
   */
  variantId?: string | null;
  fileName: string;
  mimeType: string;
  buffer: Buffer;
  actorId?: string;
  variantOf?: string;
  variantSuffix?: string;
}

export interface MediaAssetRecord {
  id: string;
  storageKey: string;
  publicUrl: string | null;
  sizeBytes: number;
  mimeType: string;
  fileName: string;
  storageConfigId: string;
}

const PURGE_MAX_ATTEMPTS = 5;

/**
 * Owns every object the platform has written, on any backend.
 *
 * Two invariants hold this together:
 *
 * - **`storageConfigId` is set at write time and never changes.** Reads and
 *   deletes route through the asset's own config, which is why a merchant can
 *   switch storage backends without moving a byte and without breaking a
 *   single existing URL (plan §5).
 * - **Usage totals move by atomic SQL increments inside the same transaction
 *   as the row.** Read-modify-write loses bytes whenever two uploads land
 *   together, and the drift is permanent because nothing recomputes it.
 */
@Injectable()
export class MediaAssetService {
  private readonly logger = new Logger(MediaAssetService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver,
    /**
     * Optional to keep this service constructible in specs that only exercise
     * asset bookkeeping; Nest always binds it. Forward-referenced because
     * StorageConfigService is what provisions the backend this service needs.
     */
    @Optional()
    @Inject(forwardRef(() => StorageConfigService))
    private readonly configs?: StorageConfigService
  ) {}

  /**
   * Uploads bytes to the org's active backend and registers the asset.
   *
   * Storage first, database second, deliberately: a registered row with no
   * object behind it is a broken image and a wrong quota reading, while an
   * object with no row is inert litter that the nightly reconciliation
   * reports. Given one has to fail first, prefer the recoverable one.
   */
  async write(input: WriteAssetInput): Promise<MediaAssetRecord> {
    const config = await this.requireActiveConfig(input.organizationId);
    const adapter = await this.resolver.forConfig(config, 'upload');

    const { key, assetId } = buildMediaKey({
      organizationId: input.organizationId,
      ownerType: input.ownerType,
      ownerId: input.ownerId,
      variantId: input.variantId,
      storeId: input.storeId,
      mimeType: input.mimeType,
      fileName: input.fileName,
      variantSuffix: input.variantSuffix,
    });
    assertKeyBelongsTo(input.organizationId, key);

    const saved = await adapter.save(input.buffer, key, input.mimeType);
    const isPrivate = isPrivateOwnerType(input.ownerType);

    try {
      return await this.register({
        assetId,
        config,
        input,
        storageKey: saved.storageKey,
        publicUrl: isPrivate ? null : saved.url,
        sizeBytes: saved.sizeBytes,
        checksum: saved.checksum,
      });
    } catch (err) {
      // The row is what makes the object reachable and accountable; without
      // it the upload never happened as far as the platform is concerned, so
      // do not leave the bytes behind.
      await adapter.delete(saved.storageKey).catch(() => undefined);
      throw err;
    }
  }

  private async register(args: {
    assetId: string;
    config: ResolvedConfig;
    input: WriteAssetInput;
    storageKey: string;
    publicUrl: string | null;
    sizeBytes: number;
    checksum?: string;
  }): Promise<MediaAssetRecord> {
    const { assetId, config, input, storageKey, publicUrl, sizeBytes, checksum } = args;

    return this.prisma.$transaction(async tx => {
      const asset = await tx.mediaAsset.create({
        data: {
          id: assetId,
          organizationId: input.organizationId,
          storeId: input.storeId ?? null,
          ownerType: input.ownerType,
          ownerId: input.ownerId ?? null,
          storageConfigId: config.id,
          storageKey,
          publicUrl,
          fileName: sanitizeFileName(input.fileName),
          mimeType: input.mimeType,
          sizeBytes,
          checksum: checksum ?? null,
          status: 'active',
          variantOf: input.variantOf ?? null,
          createdBy: input.actorId ?? 'system',
          updatedBy: input.actorId ?? 'system',
        },
      });

      await this.applyUsageDelta(tx, {
        organizationId: input.organizationId,
        configId: config.id,
        mode: config.mode,
        bytes: sizeBytes,
        count: 1,
        storeId: input.storeId ?? null,
      });

      return {
        id: asset.id,
        storageKey: asset.storageKey,
        publicUrl: asset.publicUrl,
        sizeBytes: asset.sizeBytes,
        mimeType: asset.mimeType,
        fileName: asset.fileName,
        storageConfigId: asset.storageConfigId,
      };
    });
  }

  /**
   * Atomic counter movement. Never `read then write` — two concurrent uploads
   * would both read the same total and one increment would vanish, and nothing
   * in the system recomputes the number afterwards.
   *
   * BYOS bytes move the per-config counters (so the merchant can see where
   * their media lives) but not the org's quota total, because that storage is
   * billed by their provider, not by us.
   */
  async applyUsageDelta(
    tx: Prisma.TransactionClient,
    args: {
      organizationId: string;
      configId: string;
      mode: string;
      bytes: number;
      count: number;
      storeId?: string | null;
    }
  ): Promise<void> {
    await this.applyConfigUsageDelta(tx, args.configId, args.bytes, args.count);
    if (!countsAgainstQuota(args.mode)) return;
    await this.applyOrgUsageDelta(tx, args.organizationId, args.bytes, args.count);
    // Org-level assets (a merchant logo, a document) carry no storeId and
    // count against the pool without belonging to any store's share.
    if (args.storeId) await this.applyStoreUsageDelta(tx, args.storeId, args.bytes, args.count);
  }

  /** Per-store counters, so a store's usage bar never needs an aggregate. */
  async applyStoreUsageDelta(
    tx: Prisma.TransactionClient,
    storeId: string,
    bytes: number,
    count: number
  ): Promise<void> {
    if (bytes >= 0) {
      await tx.store.update({
        where: { id: storeId },
        data: {
          storageUsedBytes: { increment: BigInt(bytes) },
          storageAssetCount: { increment: count },
        },
      });
      return;
    }

    const store = await tx.store.findUniqueOrThrow({
      where: { id: storeId },
      select: { storageUsedBytes: true, storageAssetCount: true },
    });
    await tx.store.update({
      where: { id: storeId },
      data: {
        storageUsedBytes: BigInt(Math.max(0, Number(store.storageUsedBytes) + bytes)),
        storageAssetCount: Math.max(0, store.storageAssetCount + count),
      },
    });
  }

  /** Per-backend counters — always move, whoever pays for the bytes. */
  async applyConfigUsageDelta(
    tx: Prisma.TransactionClient,
    configId: string,
    bytes: number,
    count: number
  ): Promise<void> {
    await tx.merchantStorageConfig.update({
      where: { id: configId },
      data: {
        assetBytes: { increment: BigInt(bytes) },
        assetCount: { increment: count },
      },
    });
  }

  /**
   * Org quota counters only. Separate from the per-config ones because
   * migration moves an asset between backends: both config counters change,
   * while the org total moves only when the asset crosses the boundary
   * between quota-bearing and merchant-billed storage.
   */
  async applyOrgUsageDelta(
    tx: Prisma.TransactionClient,
    organizationId: string,
    bytes: number,
    count: number
  ): Promise<void> {
    const args = { organizationId, bytes, count };
    if (args.bytes >= 0) {
      await tx.organization.update({
        where: { id: args.organizationId },
        data: {
          storageUsedBytes: { increment: BigInt(args.bytes) },
          storageAssetCount: { increment: args.count },
          storageUsedAt: new Date(),
        },
      });
      return;
    }

    // Decrements clamp at zero. `{ decrement }` would happily drive the total
    // negative after a double-purge or a corrective adjustment, and a negative
    // used-bytes reads back as free quota the merchant has not paid for.
    const org = await tx.organization.findUniqueOrThrow({
      where: { id: args.organizationId },
      select: { storageUsedBytes: true, storageAssetCount: true },
    });
    await tx.organization.update({
      where: { id: args.organizationId },
      data: {
        storageUsedBytes: BigInt(Math.max(0, Number(org.storageUsedBytes) + args.bytes)),
        storageAssetCount: Math.max(0, org.storageAssetCount + args.count),
        storageUsedAt: new Date(),
      },
    });
  }

  /**
   * The org's write target, provisioning one on first use if it has none.
   *
   * A merchant never configures storage explicitly — they sign up and upload a
   * product photo. Without this, every organization created after the media
   * registry landed would fail that first upload with an error only a platform
   * admin could fix.
   */
  async requireActiveConfig(organizationId: string): Promise<ResolvedConfig> {
    const existing = await this.resolver.activeConfig(organizationId);

    /*
     * An org on local disk gets one more look, because local disk is a
     * fallback rather than a choice: if platform R2 has been configured since
     * this org was provisioned, ensureBackend() promotes it and the upload
     * lands on object storage instead. Costs nothing for orgs already on R2 or
     * BYOS — `mode` is on the row we just read, so the common path is unchanged.
     */
    if (existing && !(existing.mode === 'local_disk' && this.configs)) return existing;

    if (this.configs) {
      await this.configs.ensureBackend(organizationId);
      const provisioned = await this.resolver.activeConfig(organizationId);
      if (provisioned) return provisioned;
    }

    // ensureBackend declined to change anything (no platform R2 on this
    // environment), so the local-disk target we already had still stands.
    if (existing) return existing;

    throw new BadRequestException(
      'No storage backend is configured for this account. Connect storage before uploading media.'
    );
  }

  /**
   * Soft-delete. The object stays until the purge cron runs, and its bytes
   * keep counting against quota until then — a trashed-but-not-purged object
   * occupies real storage that someone is paying for.
   */
  async trash(assetId: string, organizationId: string, actorId = 'system'): Promise<void> {
    const asset = await this.assertOwned(assetId, organizationId);
    if (asset.status === 'trashed') return;

    await this.prisma.mediaAsset.update({
      where: { id: asset.id },
      data: { status: 'trashed', trashedAt: new Date(), updatedBy: actorId },
    });
  }

  async restoreFromTrash(assetId: string, organizationId: string, actorId = 'system'): Promise<void> {
    const asset = await this.assertOwned(assetId, organizationId);
    await this.prisma.mediaAsset.update({
      where: { id: asset.id },
      data: { status: 'active', trashedAt: null, updatedBy: actorId },
    });
  }

  /**
   * Hard delete, routed to the backend that actually holds the object.
   *
   * A failed delete never removes the row: it becomes `purge_failed` for the
   * retry job. Dropping the row would leak an object we keep paying for and
   * can no longer find.
   */
  async purge(assetId: string): Promise<boolean> {
    const asset = await this.prisma.mediaAsset.findUnique({
      where: { id: assetId },
      select: {
        id: true,
        organizationId: true,
        storageConfigId: true,
        storageKey: true,
        sizeBytes: true,
        purgeAttempts: true,
        storeId: true,
      },
    });
    if (!asset) return false;

    const config = await this.resolver.configById(asset.storageConfigId);

    try {
      const adapter = await this.resolver.forConfig(config, 'purge');
      await adapter.delete(asset.storageKey);
    } catch (err) {
      const attempts = asset.purgeAttempts + 1;
      await this.prisma.mediaAsset.update({
        where: { id: asset.id },
        data: {
          status: 'purge_failed',
          purgeAttempts: attempts,
          purgeError: err instanceof Error ? err.message : String(err),
        },
      });
      this.logger.warn(
        `Purge failed for asset ${asset.id} on config ${config.id} (attempt ${attempts}, transient=${isTransient(err)})`
      );
      return false;
    }

    await this.prisma.$transaction(async tx => {
      await tx.mediaAsset.delete({ where: { id: asset.id } });
      await this.applyUsageDelta(tx, {
        organizationId: asset.organizationId,
        configId: config.id,
        mode: config.mode,
        bytes: -asset.sizeBytes,
        count: -1,
        storeId: asset.storeId,
      });
    });

    return true;
  }

  /** Assets whose purge has failed but is still worth retrying. */
  async listPurgeRetryable(limit = 100) {
    return this.prisma.mediaAsset.findMany({
      where: { status: 'purge_failed', purgeAttempts: { lt: PURGE_MAX_ATTEMPTS } },
      select: { id: true },
      take: limit,
    });
  }

  /**
   * Authorization for every asset-scoped operation.
   *
   * Ownership is checked on the row, not on the key's path prefix: a path
   * check alone would pass for any well-formed key an attacker could guess,
   * and the config's own org is re-checked so a caller cannot reach another
   * tenant's backend through an asset id it happens to know.
   */
  async assertOwned(assetId: string, organizationId: string) {
    const asset = await this.prisma.mediaAsset.findUnique({
      where: { id: assetId },
      select: {
        id: true,
        organizationId: true,
        storageConfigId: true,
        storageKey: true,
        status: true,
        ownerType: true,
        publicUrl: true,
        sizeBytes: true,
        // Needed by the confirm path so a size correction lands on the right
        // store's counters, not just the org total.
        storeId: true,
        storageConfig: { select: { organizationId: true } },
      },
    });
    if (!asset) throw new NotFoundException(`Media asset ${assetId} not found`);
    if (asset.organizationId !== organizationId || asset.storageConfig.organizationId !== organizationId) {
      // Deliberately the same shape as a "not found" would be, so asset ids
      // cannot be probed for existence across tenants.
      throw new ForbiddenException('This media asset belongs to another organization');
    }
    return asset;
  }

  /** Signed, short-lived, never cached — documents only. */
  async downloadUrlFor(assetId: string, organizationId: string, ttlSeconds = 300): Promise<string> {
    const asset = await this.assertOwned(assetId, organizationId);
    if (asset.publicUrl) return asset.publicUrl;
    const adapter = await this.resolver.forConfigId(asset.storageConfigId, 'upload');
    return adapter.createDownloadUrl(asset.storageKey, ttlSeconds);
  }

  async adapterForAsset(assetId: string, organizationId: string): Promise<{ adapter: StorageAdapter; storageKey: string }> {
    const asset = await this.assertOwned(assetId, organizationId);
    const adapter = await this.resolver.forConfigId(asset.storageConfigId, 'upload');
    return { adapter, storageKey: asset.storageKey };
  }

  /** Per-backend breakdown for the merchant dashboard (plan §16). */
  async breakdownByOwnerType(organizationId: string) {
    const rows = await this.prisma.mediaAsset.groupBy({
      by: ['ownerType'],
      where: { organizationId, status: { in: ['reserved', 'active'] } },
      _sum: { sizeBytes: true },
      _count: { _all: true },
    });
    return rows.map(r => ({
      ownerType: r.ownerType,
      bytes: Number(r._sum.sizeBytes ?? 0),
      count: r._count._all,
    }));
  }

  async trashedBytes(organizationId: string): Promise<number> {
    const agg = await this.prisma.mediaAsset.aggregate({
      where: { organizationId, status: 'trashed' },
      _sum: { sizeBytes: true },
    });
    return Number(agg._sum.sizeBytes ?? 0);
  }
}
